import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, MessageSquare, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface MessageResult {
  id: string;
  chat_id: string;
  original_text: string;
  created_at: string;
  sender_name: string;
  sender_image?: string;
  chat_name: string;
  is_group: boolean;
}

interface GlobalMessageSearchProps {
  searchQuery: string;
  onResultClick?: () => void;
}

export const GlobalMessageSearch = ({ searchQuery, onResultClick }: GlobalMessageSearchProps) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [results, setResults] = useState<MessageResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const searchMessages = useCallback(async (query: string) => {
    if (!user || !query.trim() || query.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      // Get user's chat IDs
      const { data: participantData } = await supabase
        .from('chat_participants')
        .select('chat_id')
        .eq('user_id', user.id);

      if (!participantData || participantData.length === 0) {
        setResults([]);
        setLoading(false);
        return;
      }

      const chatIds = participantData.map(p => p.chat_id);

      // Search messages in user's chats
      const { data: messages } = await supabase
        .from('messages')
        .select('id, chat_id, original_text, created_at, sender_id')
        .in('chat_id', chatIds)
        .ilike('original_text', `%${query.trim()}%`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!messages || messages.length === 0) {
        setResults([]);
        setLoading(false);
        return;
      }

      // Get unique sender IDs and chat IDs
      const senderIds = [...new Set(messages.map(m => m.sender_id))];
      const messageChatIds = [...new Set(messages.map(m => m.chat_id))];

      // Fetch sender profiles via RPC
      const profilePromises = senderIds.map(id =>
        supabase.rpc('get_chat_participant_profile', { _profile_id: id })
      );
      const profileResults = await Promise.all(profilePromises);

      const profileMap: Record<string, { name: string; profile_image?: string }> = {};
      profileResults.forEach((r) => {
        if (!r.error && r.data && r.data.length > 0) {
          profileMap[r.data[0].id] = {
            name: r.data[0].name,
            profile_image: r.data[0].profile_image || undefined,
          };
        }
      });

      // Fetch chat info
      const { data: chatsData } = await supabase
        .from('chats')
        .select('id, name, is_group')
        .in('id', messageChatIds);

      const chatMap: Record<string, { name: string; is_group: boolean }> = {};

      if (chatsData) {
        // For non-group chats, get the other participant's name
        for (const chat of chatsData) {
          if (chat.is_group) {
            chatMap[chat.id] = { name: chat.name || 'Group', is_group: true };
          } else {
            // Get the other participant
            const { data: otherPart } = await supabase
              .from('chat_participants')
              .select('user_id')
              .eq('chat_id', chat.id)
              .neq('user_id', user.id)
              .maybeSingle();

            if (otherPart && profileMap[otherPart.user_id]) {
              chatMap[chat.id] = { name: profileMap[otherPart.user_id].name, is_group: false };
            } else {
              chatMap[chat.id] = { name: 'Chat', is_group: false };
            }
          }
        }
      }

      const mappedResults: MessageResult[] = messages.map(m => ({
        id: m.id,
        chat_id: m.chat_id,
        original_text: m.original_text,
        created_at: m.created_at || '',
        sender_name: profileMap[m.sender_id]?.name || 'Unknown',
        sender_image: profileMap[m.sender_id]?.profile_image,
        chat_name: chatMap[m.chat_id]?.name || 'Chat',
        is_group: chatMap[m.chat_id]?.is_group || false,
      }));

      setResults(mappedResults);
    } catch (error) {
      console.error('Error searching messages:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchMessages(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, searchMessages]);

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-yellow-300 dark:bg-yellow-600 rounded px-0.5">{part}</mark>
      ) : (
        part
      )
    );
  };

  const truncateAroundMatch = (text: string, query: string, maxLen = 80) => {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const idx = lowerText.indexOf(lowerQuery);
    if (idx === -1 || text.length <= maxLen) return text;

    const start = Math.max(0, idx - 20);
    const end = Math.min(text.length, idx + query.length + 40);
    let snippet = text.slice(start, end);
    if (start > 0) snippet = '…' + snippet;
    if (end < text.length) snippet = snippet + '…';
    return snippet;
  };

  if (!searchQuery.trim() || searchQuery.trim().length < 2) return null;

  return (
    <div className="border-t">
      <div className="px-4 py-2 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {t('searchMessages')}
        </span>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>

      {!loading && searched && results.length === 0 && (
        <div className="px-4 py-4 text-sm text-muted-foreground text-center">
          {t('noMessagesFound')}
        </div>
      )}

      {results.length > 0 && (
        <div className="divide-y max-h-[300px] overflow-y-auto">
          {results.map((result) => (
            <button
              key={result.id}
              onClick={() => {
                navigate(`/chat/${result.chat_id}?messageId=${result.id}`);
                onResultClick?.();
              }}
              className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors w-full text-left"
            >
              <Avatar className="h-8 w-8 mt-0.5 flex-shrink-0">
                {result.is_group ? (
                  <AvatarFallback>
                    <Users className="h-4 w-4" />
                  </AvatarFallback>
                ) : (
                  <>
                    <AvatarImage src={result.sender_image} />
                    <AvatarFallback>{result.sender_name[0]}</AvatarFallback>
                  </>
                )}
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-sm font-medium truncate">
                    {result.is_group ? result.chat_name : result.sender_name}
                  </span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {formatDistanceToNow(new Date(result.created_at), { addSuffix: true })}
                  </span>
                </div>
                {result.is_group && (
                  <span className="text-xs text-muted-foreground">{result.sender_name}: </span>
                )}
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {highlightMatch(
                    truncateAroundMatch(result.original_text, searchQuery.trim()),
                    searchQuery.trim()
                  )}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
